/**
 * FilterBar component for filtering and sorting creatives
 * Provides decision filter, sort options, and view mode toggle
 */

import {
  ArrowUpDown,
  GitCompare,
  Grid3X3,
  List,
  Search,
  SlidersHorizontal,
  Tag,
  X,
} from 'lucide-react';
import * as React from 'react';

import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/utils';
import type { AnalysisDecision, CampaignObjective, AdPlacement, MediaType } from '@/types';
import type { TaxonomySchema } from '@/types/taxonomy';

// ============================================================================
// TYPES
// ============================================================================

export type SortField = 'createdAt' | 'updatedAt' | 'name' | 'decision';
export type SortOrder = 'asc' | 'desc';
export type ViewMode = 'grid' | 'list';

export interface TaxonomyFilterValue {
  fieldId: string;
  optionId: string;
}

export interface FilterState {
  search: string;
  decision: AnalysisDecision | 'all' | 'pending';
  mediaType: MediaType | 'all';
  objective: CampaignObjective | 'all';
  placement: AdPlacement | 'all';
  taxonomyFilters: TaxonomyFilterValue[];
  sortBy: SortField;
  sortOrder: SortOrder;
}

export interface FilterBarProps {
  filters: FilterState;
  onFilterChange: (filters: FilterState) => void;
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
  isCompareMode?: boolean;
  onToggleCompareMode?: () => void;
  selectedCount?: number;
  onClearSelection?: () => void;
  onCompareSelected?: () => void;
  taxonomySchema?: TaxonomySchema | null;
}

// ============================================================================
// CONSTANTS
// ============================================================================

const MEDIA_TYPE_OPTIONS: { value: MediaType | 'all'; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'video', label: 'Video' },
  { value: 'image', label: 'Image' },
];

const DECISION_OPTIONS: { value: AnalysisDecision | 'all' | 'pending'; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'Scale', label: 'Scale' },
  { value: 'Fix', label: 'Fix' },
  { value: 'Kill', label: 'Kill' },
  { value: 'pending', label: 'Pending' },
];

const OBJECTIVE_OPTIONS: { value: CampaignObjective | 'all'; label: string }[] = [
  { value: 'all', label: 'All Objectives' },
  { value: 'Awareness', label: 'Awareness' },
  { value: 'Consideration', label: 'Consideration' },
  { value: 'Performance', label: 'Performance' },
];

const PLACEMENT_OPTIONS: { value: AdPlacement | 'all'; label: string }[] = [
  { value: 'all', label: 'All Placements' },
  { value: 'Reels', label: 'Reels' },
  { value: 'Feed', label: 'Feed' },
  { value: 'Stories', label: 'Stories' },
];

const SORT_OPTIONS: { value: SortField; label: string }[] = [
  { value: 'createdAt', label: 'Date Created' },
  { value: 'updatedAt', label: 'Last Updated' },
  { value: 'name', label: 'Name' },
  { value: 'decision', label: 'Decision' },
];

// ============================================================================
// FILTER BAR COMPONENT
// ============================================================================

export function FilterBar({
  filters,
  onFilterChange,
  viewMode,
  onViewModeChange,
  isCompareMode = false,
  onToggleCompareMode,
  selectedCount = 0,
  onClearSelection,
  onCompareSelected,
  taxonomySchema,
}: FilterBarProps) {
  const [showAdvanced, setShowAdvanced] = React.useState(false);
  const [showTaxonomyFilters, setShowTaxonomyFilters] = React.useState(false);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onFilterChange({ ...filters, search: e.target.value });
  };

  const handleMediaTypeChange = (mediaType: MediaType | 'all') => {
    onFilterChange({ ...filters, mediaType });
  };

  const handleDecisionChange = (decision: AnalysisDecision | 'all' | 'pending') => {
    onFilterChange({ ...filters, decision });
  };

  const handleObjectiveChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onFilterChange({ ...filters, objective: e.target.value as CampaignObjective | 'all' });
  };

  const handlePlacementChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onFilterChange({ ...filters, placement: e.target.value as AdPlacement | 'all' });
  };

  const handleSortChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onFilterChange({ ...filters, sortBy: e.target.value as SortField });
  };

  const toggleSortOrder = () => {
    onFilterChange({
      ...filters,
      sortOrder: filters.sortOrder === 'asc' ? 'desc' : 'asc',
    });
  };

  const handleTaxonomyFilterChange = (fieldId: string, optionId: string | null) => {
    const currentFilters = filters.taxonomyFilters || [];
    let newFilters: TaxonomyFilterValue[];

    if (optionId === null) {
      // Remove filter for this field
      newFilters = currentFilters.filter((f) => f.fieldId !== fieldId);
    } else {
      // Check if we're updating an existing filter or adding a new one
      const existingIndex = currentFilters.findIndex((f) => f.fieldId === fieldId);
      if (existingIndex >= 0) {
        // Update existing
        newFilters = [...currentFilters];
        newFilters[existingIndex] = { fieldId, optionId };
      } else {
        // Add new
        newFilters = [...currentFilters, { fieldId, optionId }];
      }
    }

    onFilterChange({ ...filters, taxonomyFilters: newFilters });
  };

  const hasActiveFilters =
    filters.decision !== 'all' ||
    filters.mediaType !== 'all' ||
    filters.objective !== 'all' ||
    filters.placement !== 'all' ||
    filters.search.length > 0 ||
    (filters.taxonomyFilters?.length ?? 0) > 0;

  const hasTaxonomyFilters = (filters.taxonomyFilters?.length ?? 0) > 0;

  const clearFilters = () => {
    onFilterChange({
      search: '',
      decision: 'all',
      mediaType: 'all',
      objective: 'all',
      placement: 'all',
      taxonomyFilters: [],
      sortBy: 'createdAt',
      sortOrder: 'desc',
    });
  };

  return (
    <div className="space-y-4">
      {/* Main filter row */}
      <div className="flex flex-col md:flex-row md:items-center gap-4">
        {/* Search */}
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted" />
          <input
            type="text"
            placeholder="Search creatives..."
            value={filters.search}
            onChange={handleSearchChange}
            className="w-full h-10 pl-10 pr-4 rounded-lg border border-border-subtle bg-surface text-sm placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand"
          />
          {filters.search && (
            <button
              onClick={() => onFilterChange({ ...filters, search: '' })}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted hover:text-secondary"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Media type filter tabs */}
        <div className="flex items-center gap-1 p-1 rounded-lg bg-surface-subtle">
          {MEDIA_TYPE_OPTIONS.map((option) => (
            <button
              key={option.value}
              onClick={() => handleMediaTypeChange(option.value)}
              className={cn(
                'px-3 py-1.5 rounded-md text-xs font-medium transition-colors',
                filters.mediaType === option.value
                  ? 'bg-surface text-primary shadow-sm'
                  : 'text-secondary hover:text-primary'
              )}
            >
              {option.label}
            </button>
          ))}
        </div>

        {/* Decision filter tabs */}
        <div className="flex items-center gap-1 p-1 rounded-lg bg-surface-subtle">
          {DECISION_OPTIONS.map((option) => (
            <button
              key={option.value}
              onClick={() => handleDecisionChange(option.value)}
              className={cn(
                'px-3 py-1.5 rounded-md text-xs font-medium transition-colors',
                filters.decision === option.value
                  ? 'bg-surface text-primary shadow-sm'
                  : 'text-secondary hover:text-primary'
              )}
            >
              {option.label}
            </button>
          ))}
        </div>

        {/* Right side controls */}
        <div className="flex items-center gap-2 ml-auto">
          {/* Advanced filters toggle */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowAdvanced(!showAdvanced)}
            className={cn(showAdvanced && 'text-brand')}
          >
            <SlidersHorizontal className="h-4 w-4 mr-2" />
            Filters
            {(filters.objective !== 'all' || filters.placement !== 'all') && (
              <span className="ml-1.5 px-1.5 py-0.5 rounded-full bg-brand text-inverse text-xs">
                {[
                  filters.objective !== 'all',
                  filters.placement !== 'all',
                ].filter(Boolean).length}
              </span>
            )}
          </Button>

          {/* Taxonomy filters toggle (only show if schema exists) */}
          {taxonomySchema && taxonomySchema.fields.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowTaxonomyFilters(!showTaxonomyFilters)}
              className={cn((showTaxonomyFilters || hasTaxonomyFilters) && 'text-brand')}
            >
              <Tag className="h-4 w-4 mr-2" />
              Taxonomy
              {hasTaxonomyFilters && (
                <span className="ml-1.5 px-1.5 py-0.5 rounded-full bg-brand text-inverse text-xs">
                  {filters.taxonomyFilters.length}
                </span>
              )}
            </Button>
          )}

          {/* Sort dropdown */}
          <div className="flex items-center gap-1">
            <select
              value={filters.sortBy}
              onChange={handleSortChange}
              className="h-9 px-3 rounded-lg border border-border-subtle bg-surface text-xs focus:outline-none focus:ring-2 focus:ring-brand/20"
            >
              {SORT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <button
              onClick={toggleSortOrder}
              className="h-9 w-9 rounded-lg border border-border-subtle bg-surface flex items-center justify-center hover:bg-surface-subtle transition-colors"
              title={filters.sortOrder === 'asc' ? 'Ascending' : 'Descending'}
            >
              <ArrowUpDown
                className={cn(
                  'h-4 w-4 text-secondary transition-transform',
                  filters.sortOrder === 'asc' && 'rotate-180'
                )}
              />
            </button>
          </div>

          {/* Compare mode toggle */}
          <Button
            variant={isCompareMode ? 'primary' : 'ghost'}
            size="sm"
            onClick={onToggleCompareMode}
          >
            <GitCompare className="h-4 w-4 mr-2" />
            Compare
          </Button>

          {/* View mode toggle */}
          <div className="flex items-center gap-1 p-1 rounded-lg bg-surface-subtle">
            <button
              onClick={() => onViewModeChange('grid')}
              className={cn(
                'p-1.5 rounded-md transition-colors',
                viewMode === 'grid'
                  ? 'bg-surface text-primary shadow-sm'
                  : 'text-muted hover:text-secondary'
              )}
              title="Grid view"
            >
              <Grid3X3 className="h-4 w-4" />
            </button>
            <button
              onClick={() => onViewModeChange('list')}
              className={cn(
                'p-1.5 rounded-md transition-colors',
                viewMode === 'list'
                  ? 'bg-surface text-primary shadow-sm'
                  : 'text-muted hover:text-secondary'
              )}
              title="List view"
            >
              <List className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Advanced filters row */}
      {showAdvanced && (
        <div className="flex flex-wrap items-center gap-3 p-4 rounded-lg bg-surface-subtle">
          <div className="flex items-center gap-2">
            <label className="text-xs text-secondary">Objective:</label>
            <select
              value={filters.objective}
              onChange={handleObjectiveChange}
              className="h-8 px-2 rounded-md border border-border-subtle bg-surface text-xs focus:outline-none focus:ring-2 focus:ring-brand/20"
            >
              {OBJECTIVE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-2">
            <label className="text-xs text-secondary">Placement:</label>
            <select
              value={filters.placement}
              onChange={handlePlacementChange}
              className="h-8 px-2 rounded-md border border-border-subtle bg-surface text-xs focus:outline-none focus:ring-2 focus:ring-brand/20"
            >
              {PLACEMENT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="ml-auto text-xs text-muted hover:text-secondary flex items-center gap-1"
            >
              <X className="h-3 w-3" />
              Clear all
            </button>
          )}
        </div>
      )}

      {/* Taxonomy filters row */}
      {showTaxonomyFilters && taxonomySchema && taxonomySchema.fields.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 p-4 rounded-lg bg-surface-subtle border border-brand/20">
          <Tag className="h-4 w-4 text-brand" />
          {taxonomySchema.fields.map((field) => {
            const selectedFilter = filters.taxonomyFilters?.find(
              (f) => f.fieldId === field.fieldId
            );
            return (
              <div key={field.fieldId} className="flex items-center gap-2">
                <label className="text-xs text-secondary">{field.name}:</label>
                <select
                  value={selectedFilter?.optionId ?? ''}
                  onChange={(e) =>
                    handleTaxonomyFilterChange(
                      field.fieldId,
                      e.target.value || null
                    )
                  }
                  className="h-8 px-2 rounded-md border border-border-subtle bg-surface text-xs focus:outline-none focus:ring-2 focus:ring-brand/20"
                >
                  <option value="">All</option>
                  {field.options.map((option) => (
                    <option key={option.optionId} value={option.optionId}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            );
          })}

          {hasTaxonomyFilters && (
            <button
              onClick={() => onFilterChange({ ...filters, taxonomyFilters: [] })}
              className="ml-auto text-xs text-muted hover:text-secondary flex items-center gap-1"
            >
              <X className="h-3 w-3" />
              Clear taxonomy filters
            </button>
          )}
        </div>
      )}

      {/* Selection bar */}
      {selectedCount > 0 && (
        <div className="flex items-center justify-between p-3 rounded-lg bg-brand-soft border border-brand/20">
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-primary">
              {selectedCount} creative{selectedCount !== 1 ? 's' : ''} selected
            </span>
            <button
              onClick={onClearSelection}
              className="text-xs text-secondary hover:text-primary"
            >
              Clear selection
            </button>
          </div>
          <Button
            variant="primary"
            size="sm"
            onClick={onCompareSelected}
            disabled={selectedCount < 2 || selectedCount > 5}
          >
            Compare Selected
          </Button>
        </div>
      )}
    </div>
  );
}
